'use client';
import { useRouter } from 'next/compat/router';
import { useSearchParams } from 'next/navigation';
import { type FC } from 'react';

import { type SkuTier } from '../../__generated-gql-types__/globalTypes';
import { LexSlug, useLex } from '../../context/LexContext';
import { SUBSCRIBER_DUMMY_COACH_URN } from '../../utils/constants';
import OfferingPackagePage from '../package/OfferingPackagePage';
import NotFoundPage from '../page/NotFoundPage';

import { usePackagePageGetCoachByUrnQuery } from './__generated-gql-types__/PackagePage.generated';
import {
  type ApplicantPlanPageOfferingPackageFragment,
  useApplicantPlanPageOfferingPackageBySlugQuery,
} from './__generated-gql-types__/[packageSlug].generated';

interface ApplicantPlanPageProps {
  offeringPackage: Possible<ApplicantPlanPageOfferingPackageFragment>;
}

const ApplicantPlanPage: FC<ApplicantPlanPageProps> = ({ offeringPackage }) => {
  const router = useRouter();
  const { isLexEnabled } = useLex();
  const isRampCoachSetPriceEnabled = isLexEnabled(LexSlug.RAMP_COACH_SET_PRICE);

  const searchParams = useSearchParams();
  const opportunityCoachId = searchParams.get('opportunityCoachId');

  const tierToUse = searchParams.get('coachTier') as Optional<SkuTier>;

  const { data: clientData } = useApplicantPlanPageOfferingPackageBySlugQuery({
    variables: {
      coach: SUBSCRIBER_DUMMY_COACH_URN,
      slug: offeringPackage?.slug ?? '',
      categorySlugOrUrn: offeringPackage?.category?.id ?? '',
      visibleTierOnly: !tierToUse,
      tier: tierToUse,
    },
    skip:
      !offeringPackage?.category || !tierToUse || isRampCoachSetPriceEnabled,
  });

  const { data: coachData } = usePackagePageGetCoachByUrnQuery({
    variables: {
      id: opportunityCoachId ?? '',
    },
    skip: !opportunityCoachId || !isRampCoachSetPriceEnabled,
  });

  const offeringPackageToUse =
    clientData?.offeringPackageBySlug ?? offeringPackage;

  if (!offeringPackageToUse) {
    return <NotFoundPage entity="package" />;
  }

  // TODO: Move redirect to server side when ramped
  if (opportunityCoachId && isRampCoachSetPriceEnabled) {
    void router?.push(
      `/coach/${coachData?.coach?.slug}/p/${offeringPackageToUse.slug}`,
    );
  }

  return (
    <OfferingPackagePage
      offeringPackage={offeringPackageToUse}
      coachPackages={null}
    />
  );
};

export default ApplicantPlanPage;
